import { useContext, useEffect, useRef, useState } from "react";
import "simplebar-react/dist/simplebar.min.css";
import { Context } from "../context/Context";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "./help.css";
import Breadcrumb from "./breadcrumb/Breadcrumb";

const defaultFileIcons = {
  jpg: "/assets/img/elements/file-icons/pic.png",
  jpeg: "/assets/img/elements/file-icons/pic.png",
  png: "/assets/img/elements/file-icons/pic.png",
  gif: "/assets/img/elements/file-icons/pic.png",
  svg: "/assets/img/elements/file-icons/pic.png",

  mp4: "/assets/img/elements/file-icons/video.png",
  mov: "/assets/img/elements/file-icons/video.png",
  avi: "/assets/img/elements/file-icons/video.png",
  mkv: "/assets/img/elements/file-icons/video.png",

  pdf: "/assets/img/elements/file-icons/pdf.png",
  docx: "/assets/img/elements/file-icons/word.png",
  doc: "/assets/img/elements/file-icons/word.png",
  xlsx: "/assets/img/elements/file-icons/excel.png",
  xls: "/assets/img/elements/file-icons/excel.png",
  pptx: "/assets/img/elements/file-icons/other.png",
  ppt: "/assets/img/elements/file-icons/other.png",

  mp3: "/assets/img/elements/file-icons/other.png",
  wav: "/assets/img/elements/file-icons/other.png",
  aac: "/assets/img/elements/file-icons/other.png",

  zip: "/assets/img/elements/file-icons/zip.png",
  rar: "/assets/img/elements/file-icons/zip.png",
  tar: "/assets/img/elements/file-icons/zip.png",
  gz: "/assets/img/elements/file-icons/zip.png",

  js: "/assets/img/elements/file-icons/other.png",
  json: "/assets/img/elements/file-icons/other.png",
  css: "/assets/img/elements/file-icons/other.png",
  html: "/assets/img/elements/file-icons/other.png",
  txt: "/assets/img/elements/file-icons/other.png",

  default: "/assets/img/elements/file-icons/other.png",
};

const Help = ({ setBackUrl, subpath }) => {
  const { helpContent } = useContext(Context);

  const [content, setContent] = useState([]);
  const [streamFiles, setStreamFiles] = useState({});
  const [breadcrumbItems, setBreadcrumbItems] = useState([
    { label: "راهنما", path: "/settings/help" },
  ]);
  const [viewMode, setViewMode] = useState(() => {
    // ذخیره حالت نمایش در localStorage
    return localStorage.getItem("helpViewMode") || "list";
  });

  const videoContainerRef = useRef(null);
  const currentPathRef = useRef(subpath ? `help/${subpath}` : "");

  const navigate = useNavigate();

  // ذخیره حالت نمایش در localStorage هنگام تغییر
  useEffect(() => {
    localStorage.setItem("helpViewMode", viewMode);
  }, [viewMode]);

  useEffect(() => {
    const processedRawData = {};
    const initialContent = processData(helpContent, processedRawData);

    setContent(initialContent);

    if (subpath) {
      const initialPath = `help/${subpath}`;
      loadPath(initialPath, initialContent);
    } else {
      setContent(initialContent);
      currentPathRef.current = "/settings/help";
      setBreadcrumbItems([{ label: "راهنما", path: "/settings/help" }]);
    }
  }, [helpContent, subpath]);

  const processData = (data, processedRawData) => {
    if (!Array.isArray(data)) {
      return [];
    }
    const rootItems = [];
    data.forEach((item) => {
      processedRawData[item.path] = item;

      if (item.type === "directory") {
        const directoryIcon =
          item.icon || "/assets/img/elements/file-icons/folder.png";
        const processedChildren = processData(item.children, processedRawData);
        processedRawData[item.path].children = processedChildren;
        rootItems.push({
          ...item,
          icon: directoryIcon,
          children: processedChildren,
        });
      } else if (item.type === "file") {
        const extension = item.extension?.toLowerCase();
        const icon =
          item.icon || defaultFileIcons[extension] || defaultFileIcons.default;
        rootItems.push({
          ...item,
          icon: icon,
        });
      } else if (item.link_info) {
        rootItems.push(item);
      }
    });
    return rootItems.filter(Boolean);
  };

  const findItemByPath = (dataArray, targetPath) => {
    if (!dataArray) return null;
    for (const item of dataArray) {
      if (item.path === targetPath) {
        return item;
      }
      if (item.children) {
        const foundInChildren = findItemByPath(item.children, targetPath);
        if (foundInChildren) {
          return foundInChildren;
        }
      }
    }
    return null;
  };

  const loadPath = (path, allData = helpContent) => {
    const item = findItemByPath(allData, path);

    if (!item) {
      console.error(`Path not found or not a directory: ${path}`);
      navigate("/settings/help");
      return;
    }

    let newBreadcrumbs = [{ label: "راهنما", path: "/settings/help" }];
    let currentPath = "/settings/help";

    const pathSegments = path
      .split("/")
      .filter(
        (segment) => segment && segment !== "settings" && segment !== "help"
      );

    pathSegments.forEach((segment, index) => {
      const segmentPath = `${currentPath}/${segment}`;
      const existingItem = findItemByPath(allData, segmentPath);

      if (existingItem) {
        currentPath = existingItem.path;
        newBreadcrumbs.push({ label: existingItem.label, path: currentPath });
      } else {
        currentPath = segmentPath;
        newBreadcrumbs.push({
          label: segment.charAt(0).toUpperCase() + segment.slice(1),
          path: currentPath,
        });
      }
    });

    setBreadcrumbItems(newBreadcrumbs);

    let directoryChildren = [
      ...(item.children ?? []),
      ...(item.link_info ?? []),
    ];
    setContent(directoryChildren);
    currentPathRef.current = path;
  };

  const handleDirectoryClick = (directory) => {
    const newPath = directory.path;
    if (!newPath) {
      console.error("Directory is missing a path:", directory);
      return;
    }

    const currentPathForBackURL = subpath
      ? `/settings/help/${subpath}`
      : window.location.pathname.replace("/settings/help/", "");

    setBackUrl(currentPathForBackURL);

    navigate(
      `/settings/help/${newPath.replace("help/", "").replace(/\s+/g, "_")}`
    );
    const initialPath = newPath;
    loadPath(initialPath, content);
  };

  const handleStreamFile = (item) => {
    setStreamFiles((prev) => ({
      ...prev,
      [subpath]: {
        url: item.url,
        name: item.name,
      },
    }));

    if (videoContainerRef.current) {
      videoContainerRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleBreadcrumbClick = (clickedItem, index) => {
    const newBreadcrumbs = breadcrumbItems.slice(0, index + 1);
    setBreadcrumbItems(newBreadcrumbs);

    const targetPath = clickedItem.path;
    loadPath(targetPath, helpContent);
    navigate(targetPath);
  };

  // فرمت کردن سایز فایل
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "";
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  // رندر حالت Grid (کارتی)
  const renderGridItem = (item) => {
    if (item.type === "directory") {
      return (
        <div
          className="p-1 text-center clickable-item"
          key={item.path}
          onClick={() => handleDirectoryClick(item)}
        >
          <div
            className="avatar rounded-3 p-1"
            style={{
              width: "6rem",
              height: "6rem",
            }}
          >
            <img src={item.icon} className="rounded" alt={item.name} />
          </div>
          <div
            dir="rtl"
            className="text-truncate"
            style={{ width: "6rem", marginTop: "0.5rem" }}
          >
            {item.name}
          </div>
        </div>
      );
    } else if (item.type === "file") {
      const downloadUrl = `/api/download/${item.path}`;
      return (
        <div className="p-1 text-center" key={item.path}>
          <a
            className="text-dark text-decoration-none"
            href={downloadUrl}
            download={item.name + "." + item.extension}
            target="_blank"
            rel="noopener noreferrer"
          >
            <div
              className="avatar rounded-3 p-1"
              style={{
                width: "6rem",
                height: "6rem",
              }}
            >
              <img src={item.icon} className="rounded" alt={item.name} />
            </div>
            <div
              dir="rtl"
              className="text-truncate"
              style={{ width: "6rem", marginTop: "0.5rem" }}
            >
              {item.name + `.${item.extension}`}
            </div>
            {item.size && (
              <small className="text-muted d-block" style={{ fontSize: "10px" }}>
                {formatFileSize(item.size)}
              </small>
            )}
          </a>
        </div>
      );
    } else if (item.url) {
      return (
        <div
          className="p-1 text-center clickable-item"
          onClick={() => handleStreamFile(item)}
          key={item.url || item.path}
        >
          <div
            className="avatar rounded-3 p-1"
            style={{
              width: "6rem",
              height: "6rem",
            }}
          >
            <img
              src={item.icon || "/assets/img/elements/file-icons/video.png"}
              className="rounded"
              alt={item.name}
            />
          </div>
          {item.name ? (
            <div
              dir="rtl"
              className="text-truncate"
              style={{ width: "6rem", marginTop: "0.5rem" }}
            >
              {item.name}
            </div>
          ) : null}
        </div>
      );
    }
    return null;
  };

  // رندر حالت List (لیستی)
  const renderListItem = (item) => {
    if (item.type === "directory") {
      return (
        <div
          className="clickable-item list-item"
          key={item.path}
          onClick={() => handleDirectoryClick(item)}
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0.4rem 1rem",
            // borderBottom: "1px solid #e9ecef",
            cursor: "pointer",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8f9fa")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
        >
          <img
            src={item.icon}
            alt={item.name}
            style={{ width: "32px", height: "32px", marginLeft: "1rem" }}
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500 }}>{item.name}</div>
            <small className="text-muted">پوشه</small>
          </div>
          <div style={{ color: "#6c757d" }}>
            <i className="bi bi-chevron-left"></i>
          </div>
        </div>
      );
    } else if (item.type === "file") {
      const downloadUrl = `/api/download/${item.path}`;
      return (
        <div
          className="list-item"
          key={item.path}
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0.4rem 1rem",
            // borderBottom: "1px solid #e9ecef",
          }}
        >
          <img
            src={item.icon}
            alt={item.name}
            style={{ width: "32px", height: "32px", marginLeft: "1rem" }}
          />
          <div style={{ flex: 1 }}>
            <a
              href={downloadUrl}
              download={item.name + "." + item.extension}
              target="_blank"
              rel="noopener noreferrer"
              className="text-dark text-decoration-none"
            >
              <div style={{ fontWeight: 500 }}>{item.name}.{item.extension}</div>
            </a>
            {item.size && (
              <small className="text-muted">{formatFileSize(item.size)}</small>
            )}
          </div>
        </div>
      );
    } else if (item.url) {
      return (
        <div
          className="clickable-item list-item"
          onClick={() => handleStreamFile(item)}
          key={item.url || item.path}
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0.4rem 1rem",
            // borderBottom: "1px solid #e9ecef",
            cursor: "pointer",
          }}
        >
          <img
            src={item.icon || "/assets/img/elements/file-icons/video.png"}
            alt={item.name}
            style={{ width: "32px", height: "32px", marginLeft: "1rem" }}
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500 }}>{item.name || "ویدیو"}</div>
            <small className="text-muted">ویدیو استریم</small>
          </div>
        </div>
      );
    }
    return null;
  };

  const ViewModeToggle = () => (
    <div className="d-flex justify-content-between align-items-center mb-3">
      <div></div>
      <div className="btn-group" role="group">
        <button
          type="button"
          className={`btn btn-sm ${viewMode === "grid" ? "btn-outline-success-app" : "btn-outline-secondary"}`}
          onClick={() => setViewMode("grid")}
          style={{ borderRadius: "0 0.375rem  0.375rem 0" }}
        >
          <i className="bx bx-grid-alt"></i>
        </button>
        <button
          type="button"
          className={`btn btn-sm ${viewMode === "list" ? "btn-outline-success-app" : "btn-outline-secondary"}`}
          onClick={() => setViewMode("list")}
          style={{ borderRadius: "0.375rem 0 0 0.375rem" }}
        >
          <i className="bx bx-list-ul"></i>
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Breadcrumb items={breadcrumbItems} onNavigate={handleBreadcrumbClick} />
      
      <ViewModeToggle />
      
      <div
        className={viewMode === "grid" 
          ? "d-flex justify-content-start flex-row main-folder flex-wrap" 
          : "main-folder-list"
        }
        style={{ fontSize: "13px" }}
      >
        {content.map((item) => 
          viewMode === "grid" ? renderGridItem(item) : renderListItem(item)
        )}
      </div>
      
      {streamFiles[subpath] ? (
        <div className="card p-1 mt-3" ref={videoContainerRef}>
          <div className="h_iframe-aparat_embed_frame">
            <span style={{ display: "block", paddingTop: "57%" }}></span>
            <iframe
              src={streamFiles[subpath].url}
              allowFullScreen={true}
              webkitallowfullscreen="true"
              mozallowfullscreen="true"
              title={streamFiles[subpath].name}
            ></iframe>
          </div>

          {streamFiles[subpath].name ? (
            <div className="mt-2">
              <span className="m-0 p-0">{streamFiles[subpath].name}</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
};

export default Help;